import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n";
import { IMAGES } from "../data/services";
import { Seo } from "../components/site/Seo";
import { MaskLine, FadeUp } from "../components/site/Reveal";
import { CTA } from "../components/site/CTA";
import { ArrowUpRight } from "lucide-react";

const projects = {
  fr: [
    { t: "Cartographie de carrière", c: "Carrière · Cubature", img: IMAGES.quarry },
    { t: "Inspection thermique de toiture", c: "Inspection · Infrarouge", img: IMAGES.thermalRoof },
    { t: "Suivi de chantier BTP", c: "Construction · Timelapse", img: IMAGES.construction },
    { t: "Contrôle de centrale photovoltaïque", c: "Photovoltaïque · Thermique", img: IMAGES.thermalPv },
    { t: "Orthophoto & modèle 3D", c: "Topographie · RTK", img: IMAGES.ortho },
    { t: "Inspection d'éoliennes", c: "Énergie · Structures hautes", img: IMAGES.wind },
    { t: "Cartographie NDVI agricole", c: "Agriculture · Multispectral", img: IMAGES.farm },
    { t: "Inspection de lignes HT", c: "Industrie · Réseaux", img: IMAGES.powerline },
  ],
  en: [
    { t: "Quarry mapping", c: "Quarry · Volumetrics", img: IMAGES.quarry },
    { t: "Roof thermal inspection", c: "Inspection · Infrared", img: IMAGES.thermalRoof },
    { t: "Construction monitoring", c: "Building · Timelapse", img: IMAGES.construction },
    { t: "PV plant control", c: "Photovoltaic · Thermal", img: IMAGES.thermalPv },
    { t: "Orthophoto & 3D model", c: "Surveying · RTK", img: IMAGES.ortho },
    { t: "Wind turbine inspection", c: "Energy · High structures", img: IMAGES.wind },
    { t: "Agricultural NDVI mapping", c: "Agriculture · Multispectral", img: IMAGES.farm },
    { t: "HV line inspection", c: "Industry · Networks", img: IMAGES.powerline },
  ],
};

export default function Realisations() {
  const { lang } = useLang();
  const t = {
    fr: { tag: "Réalisations", title: "Nos missions, vues du ciel.", lead: "Un aperçu de nos domaines d'intervention. Vos futurs projets viendront enrichir cette sélection.", note: "Exemples représentatifs — études de cas détaillées à venir." },
    en: { tag: "Projects", title: "Our missions, from the sky.", lead: "A glimpse of our fields of intervention. Your future projects will enrich this selection.", note: "Representative examples — detailed case studies coming soon." },
  }[lang];
  const items = projects[lang];

  return (
    <>
      <Seo
        title={lang === "fr" ? "Réalisations & projets drone | Fixedrone Luxembourg" : "Projects & drone missions | Fixedrone Luxembourg"}
        description={lang === "fr" ? "Découvrez des exemples de missions Fixedrone : cartographie de carrière, thermographie de toiture, suivi de chantier, contrôle photovoltaïque, orthophoto 3D, inspection d'éoliennes et cartographie NDVI." : "Discover examples of Fixedrone missions: quarry mapping, roof thermography, construction monitoring, PV control, 3D orthophoto, wind turbine inspection and NDVI mapping."}
        keywords="réalisations drone, projets drone, études de cas, cartographie carrière, thermographie toiture, orthophoto 3D, Luxembourg"
        path="/realisations"
      />
      <section className="pt-40 pb-16 bg-[#050505] relative">
        <div className="absolute inset-0 grid-lines opacity-20" />
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 relative">
          <FadeUp><span className="font-mono-p text-xs uppercase tracking-[0.25em] text-primary-c">{t.tag}</span></FadeUp>
          <h1 className="font-display font-black text-5xl md:text-7xl tracking-tighter leading-[0.9] mt-6 max-w-4xl"><MaskLine load>{t.title}</MaskLine></h1>
          <FadeUp delay={0.1}><p className="text-white/60 mt-6 max-w-2xl text-lg leading-relaxed">{t.lead}</p></FadeUp>
          <FadeUp delay={0.15}><p className="mt-4 font-mono-p text-xs text-white/35">{t.note}</p></FadeUp>
        </div>
      </section>

      <section className="pb-24 md:pb-32 bg-[#050505]">
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {items.map((p, i) => (
            <FadeUp key={i} delay={(i % 2) * 0.06}>
              <div className={`group relative overflow-hidden clip-frame border border-white/10 ${i % 2 === 1 ? "md:mt-14" : ""}`} data-testid={`realisation-${i}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={p.img} alt={p.t} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-[1s]" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                <div className="absolute bottom-0 inset-x-0 p-6 md:p-8 flex items-end justify-between">
                  <div>
                    <span className="font-mono-p text-xs uppercase tracking-widest text-primary-c">{p.c}</span>
                    <h3 className="font-display font-semibold text-2xl md:text-3xl tracking-tight mt-2">{p.t}</h3>
                  </div>
                  <span className="w-11 h-11 rounded-full bg-white text-black flex items-center justify-center translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300"><ArrowUpRight size={18} /></span>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>
      <CTA />
    </>
  );
}
